export interface User {
  _id: string;
  id?: string;
  name: string;
  email: string;
}

export type TransactionType = 'income' | 'expense' | 'savings';

export interface BudgetCategory {
  name: string;
  type: TransactionType;
  budgetedAmount: number;
  actualAmount: number;
  sortOrder?: number;
}

export interface Budget {
  _id: string;
  id?: string;
  user: string;
  month: string;
  categories: BudgetCategory[];
  totalBudgeted: number;
  totalActual: number;
  toBeBudgetedRollover?: number;
  createdAt: string;
}

export type CreateBudgetInput = {
  month: string;
  categories?: BudgetCategory[];
  totalBudgeted?: number;
  totalActual?: number;
};

export interface Transaction {
  _id: string;
  id?: string;
  user: string;
  date: string;
  description: string;
  amount: number;
  type: TransactionType;
  category: string;
  budget?: string;
  notes?: string;
  createdAt: string;
  isDemo?: boolean;
}

export interface PaginationInfo {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
  limit: number;
}

export interface PaginatedTransactionResponse {
  transactions: Transaction[];
  pagination: PaginationInfo;
}

export interface TransactionFilters {
  startDate?: string;
  endDate?: string;
  category?: string;
  type?: string;
  description?: string;
  minAmount?: string;
  maxAmount?: string;
  page?: number;
  limit?: number;
  getAllForFilters?: boolean;
}

export interface AuthState {
  token: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  user: User | null;
  error: string | null;
}

export interface VerificationResponse {
  message: string;
  success: boolean;
}

export interface UserResponse {
  email: string;
  name: string;
  id: string;
  isVerified: boolean;
}

export interface AuthResponse {
  token: string;
  user?: {
    id: string;
    email: string;
    name: string;
  };
}

// Default category templates
export const DEFAULT_CATEGORIES: Omit<BudgetCategory, 'actualAmount'>[] = [
  // Income categories
  { name: 'Salary', type: 'income', budgetedAmount: 0 },
  { name: 'Freelance', type: 'income', budgetedAmount: 0 },
  { name: 'Investments', type: 'income', budgetedAmount: 0 },
  { name: 'Side Hustle', type: 'income', budgetedAmount: 0 },
  { name: 'Gifts', type: 'income', budgetedAmount: 0 },

  // Expense categories
  { name: 'Housing', type: 'expense', budgetedAmount: 0 },
  { name: 'Utilities', type: 'expense', budgetedAmount: 0 },
  { name: 'Groceries', type: 'expense', budgetedAmount: 0 },
  { name: 'Transportation', type: 'expense', budgetedAmount: 0 },
  { name: 'Healthcare', type: 'expense', budgetedAmount: 0 },
  { name: 'Insurance', type: 'expense', budgetedAmount: 0 },
  { name: 'Dining Out', type: 'expense', budgetedAmount: 0 },
  { name: 'Entertainment', type: 'expense', budgetedAmount: 0 },
  { name: 'Shopping', type: 'expense', budgetedAmount: 0 },
  { name: 'Personal Care', type: 'expense', budgetedAmount: 0 },
  { name: 'Fitness', type: 'expense', budgetedAmount: 0 },
  { name: 'Education', type: 'expense', budgetedAmount: 0 },
  { name: 'Subscriptions', type: 'expense', budgetedAmount: 0 },

  // Savings categories
  { name: 'Emergency Fund', type: 'savings', budgetedAmount: 0 },
  { name: 'Retirement', type: 'savings', budgetedAmount: 0 },
  { name: 'Investment Account', type: 'savings', budgetedAmount: 0 },
  { name: 'Vacation Fund', type: 'savings', budgetedAmount: 0 },
  { name: 'House Down Payment', type: 'savings', budgetedAmount: 0 },
  { name: 'Debt Payment', type: 'savings', budgetedAmount: 0 }
]; 